"use client";

import { useState } from "react";
import Modal from "@/app/components/Modal/modal";
import { ToastProvider } from "@/app/components/ToastNotification/toastProvider";
import useDeferredReviveAds from "@/app/hooks/useDeferredReviveAds";
import { publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks";
import AllPrizesGrid from "./components/allPrizesGrid/AllPrizesGrid";
import BotBPassWidget from "./components/botbPassWidget/BotBPassWidget";
import CollectionRow from "./components/collectionRow/CollectionRow";
import FilterBar from "./components/filterBar/FilterBar";
import SortSheet from "./components/filterBar/SortSheet";
import FilterOptionsModal, { type FilterModalTab } from "./components/filterModal/FilterOptionsModal";
import FiltersDrawer from "./components/filtersDrawer/FiltersDrawer";
import HowItWorksModal from "./components/hero/HowItWorksModal";
import CompetitionCountdown from "./components/CompetitionCountdown";
import PickYourPrizeHero from "./components/hero/PickYourPrizeHero";
import LifestyleLoader from "./components/lifestyleLoader";
import TrustSpecs from "./components/trustSpecs/TrustSpecs";
import useLifestylePage from "./hooks/useLifestylePage";
import { CATEGORY_LABELS } from "./lifestyle-constants";
import Header from "@/app/(general-header)/header/page";
import "./lifestyle-page.css";

/**
 * /prizes/lifestyle — the Daily prize repository.
 *
 * Thin by design: `useLifestylePage` owns the behaviour, this renders it. The
 * Umbraco grid for this node is five OurDailyPrizes macros plus an Instant Win
 * block, none of which maps onto the 2026 layout, so unlike /prizes/cars there
 * is no `withBotBGrid` dispatch here — the competition id is resolved from the
 * page URL against `/getactivecompetitions` in Redux (`LIFESTYLE_DEV_PAGE_URL` in dev).
 *
 * The site header is rendered here (not MasterReact/HeaderV2). MasterReact sets
 * `isUsingCustomHeader` for the LifestylePrizeRepositoryV2 template so the MVC
 * shell stays out of the way; this Header dispatches `sync-header-store` for
 * StoreProvider in lifestyle.js.
 */
export default function LifestylePrizesPage() {
  const dispatch = useAppDispatch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filterModalTab, setFilterModalTab] = useState<FilterModalTab | null>(null);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);

  const {
    isLoading,
    competitionEndDateTime,
    heroPrizes,
    collections,
    isFiltering,
    gridPrizes,
    visiblePrizes,
    reveal,
    filters,
    basket,
    scrollToCollection,
  } = useLifestylePage();

  const modalData = useAppSelector(state => state.modal.data);
  const isModalOpen = useAppSelector(state => state.modal.isOpen);

  useDeferredReviveAds();
  return (
    <>
      <header id="botb-header" className="lifestyle-header sticky top-0 bg-white z-[1003]">
        <Header renderModalComponentHere={false} />
      </header>
      <div id="lifestyle-page-content" className="flex flex-col w-full bg-gray-4 pb-14 lg:pb-[77px]">
        {competitionEndDateTime ? <CompetitionCountdown endDateTime={competitionEndDateTime} /> : null}
        {/* The Revive async script is injected here by useDeferredReviveAds. */}
        <div id="script_ad_server" className="hidden" />

        {isLoading ? (
          <LifestyleLoader />
        ) : (
          <>
            <PickYourPrizeHero
              prizes={heroPrizes}
              onAddToCart={basket.openTicketSelector}
              onOpenHowItWorks={() => setIsHowItWorksOpen(true)}
            />

            <FilterBar
              filters={filters}
              onOpenDrawer={() => setIsDrawerOpen(true)}
              onOpenFilterModal={setFilterModalTab}
              onOpenSortSheet={() => setIsSortSheetOpen(true)}
            />

            {isFiltering ? (
              <AllPrizesGrid
                prizes={visiblePrizes}
                totalCount={gridPrizes.length}
                // The picked category names the results when there is one, so
                // the header reads "CARS (58)" rather than a bare "RESULTS (58)".
                heading={filters.filters.category ? CATEGORY_LABELS[filters.filters.category] : "Results"}
                // Wrapped: `clear` takes an optional group, and the click event
                // must not land in it.
                onBackToLobby={() => filters.clear()}
                onAddToCart={basket.openTicketSelector}
                showAds={false}
                hasMore={reveal.hasMore}
                sentinelRef={reveal.sentinelRef}
              />
            ) : (
              <>
                {collections.map((collection, index) => (
                  <CollectionRow
                    key={collection.id}
                    collection={collection}
                    onAddToCart={basket.openTicketSelector}
                    isPriority={index === 0}
                  />
                ))}

                <AllPrizesGrid
                  prizes={visiblePrizes}
                  totalCount={gridPrizes.length}
                  heading="All prizes"
                  onAddToCart={basket.openTicketSelector}
                  showAds
                  hasMore={reveal.hasMore}
                  sentinelRef={reveal.sentinelRef}
                />
              </>
            )}

            {/* Both close out the page, in the lobby and the filtered view. */}
            <BotBPassWidget />
            <TrustSpecs />

            <FiltersDrawer isOpen={isDrawerOpen} filters={filters} onClose={() => setIsDrawerOpen(false)} />

            <FilterOptionsModal
              isOpen={filterModalTab !== null}
              initialTab={filterModalTab ?? "collections"}
              collections={collections}
              selectedCategory={filters.filters.category}
              categoryCounts={filters.categoryFacets}
              onScrollToCollection={scrollToCollection}
              onSelectCategory={filters.setCategory}
              onClose={() => setFilterModalTab(null)}
            />

            <HowItWorksModal isOpen={isHowItWorksOpen} onClose={() => setIsHowItWorksOpen(false)} />

            <SortSheet
              isOpen={isSortSheetOpen}
              selected={filters.filters.sort}
              onSelect={filters.setSort}
              onClose={() => setIsSortSheetOpen(false)}
            />
          </>
        )}

        <Modal
          isOpen={isModalOpen}
          title=""
          closeModal={event => {
            event.stopPropagation();
            dispatch(publishModalVisibility({ isOpen: false }));
          }}
          modalClassNames="md:rounded-2xl xs:absolute xs:bottom-0 xs:w-full xs:rounded-b-none xs:rounded-t-2xl z-10 !px-4 pt-12 pb-8 lg:!px-8 lg:!pb-10 lg:!pt-12 !max-h-none"
          body={modalData}
        />

        <ToastProvider />
      </div>
    </>
  );
}
